// Members, invitations and role assignments (ADR-0011).
//
// Every function here is a permission-checked RPC: the tables have no write
// policy, because "remove a member" and "grant a role" carry rules a WITH CHECK
// cannot express — the last owner cannot be removed, and an invitation binds to
// the identity that accepts it rather than to the address it was sent to.

import { supabase, callFunction } from "./supabase";
import { asAppError } from "./entitlements";

export interface Assignment {
  id: string;
  role: string;
  role_name: string;
  scope_type: "workspace" | "connection" | "project";
  scope_id: string | null;
  scope_name: string | null;
  starts_at: string | null;
  expires_at: string | null;
}

/**
 * Inside the company, or working for it from outside (ADR-0013).
 *
 * Not a role and not a permission — a Creator is a Creator either way. It is
 * the fact the People screen could not previously state, so an agency
 * contractor and an employee rendered as the same row and the only clue was
 * the email domain.
 */
export type Relationship = "internal" | "external";

export interface Person {
  user_id: string;
  email: string | null;
  is_owner: boolean;
  relationship: Relationship;
  /** The outside company they work for. A captured name, not a linked entity. */
  organisation: string | null;
  joined_at: string;
  assignments: Assignment[];
}

export interface Invitation {
  id: string;
  email: string;
  role_code: string;
  scope_type: string;
  scope_id: string | null;
  token: string;
  relationship: Relationship;
  organisation: string | null;
  /** When the access ends. `expires_at` is only how long the link stays usable. */
  access_expires_at: string | null;
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

export interface Role {
  code: string;
  name: string;
  description: string | null;
  is_owner: boolean;
  sort_order: number;
}

export async function listPeople(workspaceId: string): Promise<Person[]> {
  const { data, error } = await supabase.rpc("workspace_people", {
    p_workspace_id: workspaceId,
  });
  if (error) throw asAppError(error);
  return (data ?? []) as Person[];
}

export async function listRoles(): Promise<Role[]> {
  const { data, error } = await supabase
    .from("roles")
    .select("code, name, description, is_owner, sort_order")
    .order("sort_order");
  if (error) throw asAppError(error);
  return data as Role[];
}

/** One entry from the permission catalogue, for the role comparison table. */
export interface Permission {
  key: string;
  name: string;
  description: string | null;
  category: string;
  is_protected: boolean;
  sort_order: number;
}

/**
 * The whole role/permission grid in one shape: what the permissions are, and
 * which roles hold each. Read straight from the catalogue tables rather than a
 * copy in the client — a role added by a migration then appears in the
 * comparison without a release, and cannot drift from what the server enforces.
 */
export interface RoleMatrix {
  permissions: Permission[];
  /** role code -> the permission keys it holds. Owner is absent by design: it
   *  holds everything, including permissions added after it was granted. */
  granted: Record<string, Set<string>>;
}

export async function listRoleMatrix(): Promise<RoleMatrix> {
  const [catalogue, grants] = await Promise.all([
    supabase.from("permissions")
      .select("key, name, description, category, is_protected, sort_order")
      .order("sort_order"),
    supabase.from("role_permissions").select("role_code, permission_key"),
  ]);
  if (catalogue.error) throw asAppError(catalogue.error);
  if (grants.error) throw asAppError(grants.error);

  const granted: Record<string, Set<string>> = {};
  for (const row of (grants.data ?? []) as { role_code: string; permission_key: string }[]) {
    (granted[row.role_code] ??= new Set()).add(row.permission_key);
  }
  return { permissions: (catalogue.data ?? []) as Permission[], granted };
}

export async function listInvitations(workspaceId: string): Promise<Invitation[]> {
  const { data, error } = await supabase
    .from("workspace_invitations")
    .select("*")
    .eq("workspace_id", workspaceId)
    .is("accepted_at", null)
    .is("revoked_at", null)
    .order("created_at", { ascending: false });
  if (error) throw asAppError(error);
  return data as Invitation[];
}

export interface EmailOutcome {
  status: "sent" | "skipped" | "failed" | "not_requested";
  provider?: string;
  reason?: string;
}

export interface InviteResult {
  invitation: Invitation;
  invite_url: string;
  email: EmailOutcome;
}

/**
 * Creates the invitation and, when asked, emails it.
 *
 * Goes through the `invite` Edge Function rather than the RPC directly, because
 * only the server may hold SMTP credentials and render templates. The
 * permission check still happens in the database — the function calls the same
 * `invite_member()` RPC as the caller.
 *
 * Emailing is best-effort: the invitation exists either way, and the returned
 * `email` says what actually happened so the UI can fall back to the link
 * rather than implying a message is on its way.
 */
export async function inviteMember(args: {
  workspaceId: string;
  email: string;
  role: string;
  scopeType?: "workspace" | "connection";
  scopeId?: string | null;
  sendEmail?: boolean;
  relationship?: Relationship;
  organisation?: string | null;
  /** ISO date; becomes the role assignment's expiry when they accept. */
  accessExpiresAt?: string | null;
}): Promise<InviteResult> {
  return callFunction<InviteResult>("invite", {
    workspace_id: args.workspaceId,
    email: args.email,
    role_code: args.role,
    scope_type: args.scopeType ?? "workspace",
    scope_id: args.scopeId ?? null,
    send_email: args.sendEmail ?? true,
    relationship: args.relationship ?? "internal",
    organisation: args.organisation ?? null,
    access_expires_at: args.accessExpiresAt ?? null,
  });
}

/**
 * Send the same invitation again.
 *
 * The same row and the same token: the first link may be sitting in a spam
 * folder, and issuing a second one would leave two links where only the newer
 * works. `resend_invitation()` pushes the link's expiry out to a fortnight and
 * refuses more than one send a minute, so a button pressed twice does not send
 * two emails.
 *
 * Returns the same shape as inviting, so the screen reports the outcome the
 * same way — including "not emailed, here is the link" when no provider is
 * configured.
 */
export async function resendInvitation(invitationId: string): Promise<InviteResult> {
  return callFunction<InviteResult>("invite", { invitation_id: invitationId });
}

/**
 * Reclassify someone who is already here.
 *
 * Every existing member defaults to `internal`, which is wrong for the agency
 * people already in the workspace — so this is how the record gets corrected
 * without removing and re-inviting them.
 */
export async function setMemberRelationship(args: {
  workspaceId: string;
  userId: string;
  relationship: Relationship;
  organisation?: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc("set_member_relationship", {
    p_workspace_id: args.workspaceId,
    p_user_id: args.userId,
    p_relationship: args.relationship,
    p_organisation: args.organisation ?? null,
  });
  if (error) throw asAppError(error);
}

export async function revokeInvitation(id: string): Promise<void> {
  const { error } = await supabase.rpc("revoke_invitation", { p_invitation_id: id });
  if (error) throw asAppError(error);
}

export interface AcceptResult {
  workspace_id: string;
  workspace_name: string;
  role: string;
  /** Set when the invitation granted access for a fixed term. */
  access_expires_at: string | null;
}

export async function acceptInvitation(token: string): Promise<AcceptResult> {
  const { data, error } = await supabase.rpc("accept_invitation", { p_token: token });
  if (error) throw asAppError(error);
  return data as AcceptResult;
}

/**
 * An invitation waiting for the signed-in person, found by their own confirmed
 * email address rather than by the token in a link they may no longer have.
 * The token is deliberately not part of this — accepting goes by id.
 */
export interface PendingInvitation {
  id: string;
  workspace_id: string;
  workspace_name: string;
  role_code: string;
  role_name: string;
  relationship: Relationship;
  organisation: string | null;
  invited_by_email: string | null;
  scope_type: "workspace" | "connection" | "project";
  scope_name: string | null;
  created_at: string;
  /** When the link stops working. */
  expires_at: string;
  /** When the access itself ends, if the inviter set a term. */
  access_expires_at: string | null;
}

export async function myInvitations(): Promise<PendingInvitation[]> {
  const { data, error } = await supabase.rpc("my_invitations");
  if (error) throw asAppError(error);
  return (data ?? []) as PendingInvitation[];
}

export async function acceptMyInvitation(invitationId: string): Promise<AcceptResult> {
  const { data, error } = await supabase.rpc("accept_my_invitation", {
    p_invitation_id: invitationId,
  });
  if (error) throw asAppError(error);
  return data as AcceptResult;
}

export async function grantRole(args: {
  workspaceId: string;
  userId: string;
  role: string;
  scopeType?: "workspace" | "connection";
  scopeId?: string | null;
  expiresAt?: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc("grant_role", {
    p_workspace_id: args.workspaceId,
    p_user_id: args.userId,
    p_role_code: args.role,
    p_scope_type: args.scopeType ?? "workspace",
    p_scope_id: args.scopeId ?? null,
    p_expires_at: args.expiresAt ?? null,
    p_note: null,
  });
  if (error) throw asAppError(error);
}

/**
 * Replace one grant with another — role, scope or end date — in one server
 * transaction, so the person never holds neither or both mid-edit.
 */
export async function changeRoleAssignment(args: {
  assignmentId: string;
  role: string;
  scopeType: "workspace" | "connection";
  scopeId: string | null;
  expiresAt: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc("change_role_assignment", {
    p_assignment_id: args.assignmentId,
    p_role_code: args.role,
    p_scope_type: args.scopeType,
    p_scope_id: args.scopeId,
    p_expires_at: args.expiresAt,
  });
  if (error) throw asAppError(error);
}

/**
 * Grant a role on every account filed under a brand right now (ADR-0029: a
 * brand is filing, never authority). Writes one connection-scoped grant per
 * account; accounts filed under the brand later are not covered. Returns how
 * many accounts it reached.
 */
export async function grantRoleForBrand(args: {
  workspaceId: string;
  userId: string;
  role: string;
  brandId: string;
  expiresAt?: string | null;
}): Promise<number> {
  const { data, error } = await supabase.rpc("grant_role_for_brand", {
    p_workspace_id: args.workspaceId,
    p_user_id: args.userId,
    p_role_code: args.role,
    p_brand_id: args.brandId,
    p_expires_at: args.expiresAt ?? null,
  });
  if (error) throw asAppError(error);
  return data as number;
}

export async function revokeRole(assignmentId: string): Promise<void> {
  const { error } = await supabase.rpc("revoke_role", { p_assignment_id: assignmentId });
  if (error) throw asAppError(error);
}

/**
 * What removing somebody actually touched.
 *
 * On an agency workspace, Remove is not local: every grant this agency threaded
 * out to a client goes with it. `retained` is what deliberately did *not* go —
 * access the client granted directly, which is not the agency's to revoke. It
 * has to be shown, because "removed" over a retained grant is the failure this
 * return value exists to prevent.
 */
export type RemovalOutcome = {
  clients_affected: number;
  assignments_removed: number;
  retained: {
    client_workspace_id: string;
    client_name: string;
    roles: string[];
    reason: string;
  }[];
};

export async function removeMember(
  workspaceId: string, userId: string,
): Promise<RemovalOutcome> {
  const { data, error } = await supabase.rpc("remove_member", {
    p_workspace_id: workspaceId,
    p_user_id: userId,
  });
  if (error) throw asAppError(error);
  return data as RemovalOutcome;
}

/** The link to send. There is no email delivery yet — this is copied by hand. */
export const invitationLink = (token: string): string =>
  `${window.location.origin}/join?token=${encodeURIComponent(token)}`;
